import { useCallback, useEffect, useMemo } from "react";
import { AlertCircle, Mail, Paperclip } from "lucide-react";
import type { SendEmailRequest } from "@forgepoint/shared";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { AssetDownload } from "@/components/activityEvents/AssetDownload";
import { useStandardAgentSettingsStore } from "@/store/standardAgentSettingsStore";
import { useStandardAgentStore } from "@/store/standardAgentStore";
import { useTeamsStore } from "@/store/teamsStore";
import { cn } from "@/lib/utils";
import { SendEmailBody } from "./SendEmailBody";
import { EmailAddressRow } from "./EmailAddressRow";
import {
  getSendEmailMode,
  getSendEmailModeLabels,
  isExternalEmailAddress,
} from "./helpers/richActionHelpers";

interface SendEmailTaskReviewProps {
  taskId: string;
  request: SendEmailRequest;
}

/**
 * Review body for a `SendEmail` task: the email as the agent requested it, the thread it continues and
 * what that thread fills in, plus the reason the last approved send failed, if any.
 * @param props - The task id and the email request under review
 * @returns The rendered email review
 */
export function SendEmailTaskReview({
  taskId,
  request,
}: SendEmailTaskReviewProps) {
  const labels = getSendEmailModeLabels(getSendEmailMode(request));
  const assetIds = request.assetIds ?? [];
  const sendError = useStandardAgentStore((state) =>
    state.sendErrorByTaskId.get(taskId),
  );
  const settings = useStandardAgentSettingsStore((state) => state.settings);
  const fetchSettings = useStandardAgentSettingsStore(
    (state) => state.fetchSettings,
  );
  const userMap = useTeamsStore((state) => state.userMap);
  const ensureMembers = useTeamsStore((state) => state.ensureMembers);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  useEffect(() => {
    ensureMembers();
  }, [ensureMembers]);

  const memberEmails = useMemo(
    () =>
      new Set([...userMap.values()].map((user) => user.email.toLowerCase())),
    [userMap],
  );
  const isExternal = useCallback(
    (address: string) =>
      isExternalEmailAddress(
        address,
        settings?.internalEmailDomains ?? [],
        memberEmails,
      ),
    [settings, memberEmails],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-4 p-6">
        {sendError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to send: {sendError}</AlertDescription>
          </Alert>
        )}
        <div className="flex items-start gap-2">
          <Mail className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
          <span
            className={cn(
              "min-w-0 break-words text-lg font-semibold",
              !request.subject && "text-muted-foreground",
            )}
          >
            {request.subject || labels.subject}
          </span>
        </div>
        {labels.thread && (
          <div className="text-sm text-muted-foreground">{labels.thread}</div>
        )}

        <div className="space-y-2 text-sm">
          {settings?.agentEmailFromAddress && (
            <EmailAddressRow
              label="From"
              addresses={[settings.agentEmailFromAddress]}
              emptyLabel="None"
            />
          )}
          <EmailAddressRow
            label="To"
            addresses={request.recipients ?? []}
            emptyLabel={labels.to}
            isExternal={isExternal}
          />
          <EmailAddressRow
            label="CC"
            addresses={request.cc ?? []}
            emptyLabel={labels.cc}
            isExternal={isExternal}
          />
          <EmailAddressRow
            label="BCC"
            addresses={request.bcc ?? []}
            emptyLabel="None"
            isExternal={isExternal}
          />
        </div>
      </div>

      <Separator />

      <div className="flex-1 overflow-auto p-6">
        <SendEmailBody request={request} />
      </div>

      {assetIds.length > 0 && (
        <>
          <Separator />
          <div className="space-y-3 p-6">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Paperclip className="h-4 w-4" />
              Attachments ({assetIds.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {assetIds.map((assetId) => (
                <AssetDownload key={assetId} assetId={assetId} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
